import * as THREE from 'three';
import { isPointInRegion, type Region } from '../utils/geometry';
import { SurfaceType } from '../terrain/SurfaceType';
import type { Course } from '../course/Course';
import type { Biome } from '../course/Biome';
import { VegetationField } from '../course/Vegetation';
import { computeCourseBounds } from './computeCourseBounds';

const MAX_ATTEMPTS_PER_INSTANCE = 20;

/**
 * Turns a Biome recipe into an actual VegetationField, via seeded
 * rejection sampling over the biome's region (or the whole course's
 * bounds, if the biome doesn't specify one). Every candidate point is
 * checked against Hole.surfaceTypeAt so vegetation never spawns on
 * fairway/green/bunker regardless of what region the biome covers — a
 * biome describes "where trees/bushes/grass could grow," not "paint over
 * the playing surface."
 *
 * Determinism: same Biome.seed always produces the same population, which
 * matters for repeatable builds/tests and for not having trees jump around
 * between reloads.
 */
export class BiomeRenderer {
  populate(course: Course, biome: Biome): VegetationField {
    const field = new VegetationField();
    const { def } = biome;
    const region: Region = def.region ?? boundsAsRegion(course);

    const bounds = computeCourseBounds(course, 0);
    const totalWeight = def.speciesMix.reduce((sum, entry) => sum + entry.weight, 0);
    if (totalWeight <= 0 || def.speciesMix.length === 0) return field;

    const area = (bounds.maxX - bounds.minX) * (bounds.maxZ - bounds.minZ);
    const targetCount = Math.max(0, Math.round(area * def.density));

    let seed = def.seed;
    const rnd = (): number => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return (seed % 10000) / 10000;
    };

    let placed = 0;
    let attempts = 0;
    const maxAttempts = targetCount * MAX_ATTEMPTS_PER_INSTANCE;

    while (placed < targetCount && attempts < maxAttempts) {
      attempts++;
      const x = bounds.minX + rnd() * (bounds.maxX - bounds.minX);
      const z = bounds.minZ + rnd() * (bounds.maxZ - bounds.minZ);
      const point = { x, z };

      if (!isPointInRegion(point, region)) continue;
      if (isOnPlayableSurface(course, point)) continue;

      field.add({
        modelId: pickWeightedSpecies(def.speciesMix, totalWeight, rnd()),
        position: new THREE.Vector3(x, 0, z),
        rotationY: def.rotationVariation ? rnd() * Math.PI * 2 : 0,
        scale: def.scaleVariation.min + rnd() * (def.scaleVariation.max - def.scaleVariation.min),
      });
      placed++;
    }

    return field;
  }
}

function isOnPlayableSurface(course: Course, point: { x: number; z: number }): boolean {
  for (const hole of course.holes) {
    const type = hole.surfaceTypeAt(point);
    if (type === SurfaceType.Fairway || type === SurfaceType.Green || type === SurfaceType.Bunker) {
      return true;
    }
  }
  return false;
}

function pickWeightedSpecies(
  speciesMix: readonly { modelId: string; weight: number }[],
  totalWeight: number,
  roll: number,
): string {
  let remaining = roll * totalWeight;
  for (const entry of speciesMix) {
    if (remaining < entry.weight) return entry.modelId;
    remaining -= entry.weight;
  }
  return speciesMix[speciesMix.length - 1].modelId;
}

function boundsAsRegion(course: Course): Region {
  const bounds = computeCourseBounds(course, 0);
  return {
    kind: 'polygon',
    points: [
      { x: bounds.minX, z: bounds.minZ },
      { x: bounds.maxX, z: bounds.minZ },
      { x: bounds.maxX, z: bounds.maxZ },
      { x: bounds.minX, z: bounds.maxZ },
    ],
  };
}
